import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { Paper } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Location } from '@/core/entities';
import { SingleLocationResponse, LocationResponse } from '@/api/locations/types';
import Backdrop from '@/components/ui/backdrop';
import { useSearchQuery } from '@/shared/hooks/useSearchQuery';
import UpdateLocationForm, {
	UpdateLocationFormModel,
} from '@/features/locations/UpdateLocationForm';
import DeleteLocationForm from '@/features/locations/DeleteLocation';
import { useLocation } from '@/queries/locations';
import { useSite } from '@/queries/sites';
import { PaginationMeta } from '@/shared/types/pagination';
import { FIRST_PAGE, PAGINATION_META_DEFAULT } from '@/shared/constants/pagination';
import AdminControls from '@/features/admin/AdminControls';
import LocationsManagement from '@/features/locations/locations-management/LocationsManagement';
import { Box } from '@mui/system';

const LocationPage: FunctionComponent = (): ReactElement => {
	const { locationId } = useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const [location, setLocation] = useState<SingleLocationResponse>();
	const [subLocations, setSubLocations] = useState<LocationResponse[]>();
	const { customer, site, ancestors, subLevel } = location || {};

	const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>(PAGINATION_META_DEFAULT);
	const { currentPage, totalPages, pageSize } = paginationMeta;

	const [searchTerm] = useState('');
	const searchQuery = useSearchQuery(searchTerm);

	const [deleteLocationForm, setDeleteLocationForm] = useState<{
		open: boolean;
		level?: 'location' | 'subLocation';
		location?: Location;
	}>({ open: false });

	const [updateLocationForm, setUpdateLocationForm] = useState<{
		open: boolean;
		formModel: UpdateLocationFormModel;
		level?: 'location' | 'subLocation';
		location?: Location;
	}>({
		open: false,
		formModel: { name: '' },
	});

	const {
		queryKey: locationQueryKey,
		query: { data: locationData },
	} = useLocation({
		locationId,
		queryParams: { limit: pageSize, search: searchQuery },
	});

	useEffect(() => {
		const location = locationData?.pages[currentPage - 1];
		if (location) setLocation({ ...location });
	}, [locationData, currentPage]);

	useEffect(() => {
		const { subLocations } =
			locationData?.pages.find((page) => page.subLocations.page === currentPage) || {};
		if (subLocations) {
			const {
				data,
				page: currentPage,
				totalItems,
				totalPages,
				itemsPerPage: pageSize,
			} = subLocations;
			setSubLocations(data);
			setPaginationMeta({ currentPage, totalItems, totalPages, pageSize });
		}
	}, [locationData, currentPage]);

	// Handles the pagination state in case when the only element on the page is removed
	useEffect(() => {
		const pagesCount = locationData?.pages.length;
		if (!pagesCount || !totalPages) return;
		if (totalPages > pagesCount)
			setPaginationMeta((paginationMeta) => ({ ...paginationMeta, currentPage: pagesCount }));
	}, [totalPages, locationData]);

	// If query params change reset page number
	useEffect(
		() => setPaginationMeta((paginationMeta) => ({ ...paginationMeta, currentPage: FIRST_PAGE })),
		[searchQuery]
	);

	const {
		query: { data: siteData },
	} = useSite(site?.id);

	const refetchLocations = async () => {
		await queryClient.invalidateQueries(locationQueryKey);
	};

	const onSubLocationUpdated = async () => refetchLocations();
	const onLocationUpdated = async () => refetchLocations();
	const onSubLocationDeleted = async () => refetchLocations();
	const onLocationDeleted = () => {
		const redirectRoute = location?.parentId
			? `/customers/${customer!.id}/locations/${location.parentId}`
			: `/customers/${customer!.id}/sites/${site!.id}`;

		navigate(redirectRoute, { replace: true });
	};

	const breadcrumbs = useMemo(() => {
		if (!customer || !site || !ancestors) return [];
		return [
			{ title: 'Customers', path: '/customers' },
			{ title: customer.name, path: `/customers/${customer.id}` },
			{ title: site.name, path: `/customers/${customer.id}/sites/${site.id}` },
			...[...ancestors].reverse().map((ancestor, key) => ({
				title: ancestor.name,
				path:
					key === ancestors.length - 1
						? undefined
						: `/customers/${customer.id}/locations/${ancestor.id}`,
			})),
		];
	}, [customer, site, ancestors]);

	if (!location || !customer || !siteData) {
		return <Backdrop />;
	}

	return (
		<AdminPanelPage
			title={location.name}
			breadcrumbs={breadcrumbs}
			headerControls={
				<AdminControls
					remove={() =>
						setDeleteLocationForm({
							...deleteLocationForm,
							location,
							level: 'location',
							open: true,
						})
					}
					edit={() =>
						setUpdateLocationForm({
							...updateLocationForm,
							location,
							level: 'location',
							formModel: { name: location.name },
							open: true,
						})
					}
				/>
			}
		>
			<Box component={Paper} padding={3}>
				<LocationsManagement
					level={location.subLevel}
					parentLocationId={location.id}
					site={siteData}
					header={{}}
					table={{
						data: subLocations || [],
						rowAction: (location) => navigate(`/customers/${customer.id}/locations/${location.id}`),
						columnActions: {
							edit: (location) =>
								setUpdateLocationForm({
									...updateLocationForm,
									location,
									level: 'subLocation',
									formModel: { name: location.name },
									open: true,
								}),
							remove: (location) =>
								setDeleteLocationForm({
									...deleteLocationForm,
									location,
									level: 'subLocation',
									open: true,
								}),
						},
					}}
					invalidateData={refetchLocations}
				/>
			</Box>
			<UpdateLocationForm
				open={updateLocationForm.open}
				onClose={() => setUpdateLocationForm({ ...updateLocationForm, open: false })}
				location={updateLocationForm.location}
				level={updateLocationForm.level === 'location' ? location.level : subLevel}
				formModel={updateLocationForm.formModel}
				onChangeFormModel={(field: keyof Location, value: string) =>
					setUpdateLocationForm({
						...updateLocationForm,
						formModel: { ...updateLocationForm.formModel, [field]: value },
					})
				}
				onSubmitted={
					updateLocationForm.level === 'location' ? onLocationUpdated : onSubLocationUpdated
				}
			/>
			<DeleteLocationForm
				open={deleteLocationForm.open}
				onClose={() => setDeleteLocationForm({ ...deleteLocationForm, open: false })}
				location={deleteLocationForm.location}
				level={deleteLocationForm.level === 'location' ? location.level : subLevel}
				onSubmitted={
					deleteLocationForm.level === 'location' ? onLocationDeleted : onSubLocationDeleted
				}
			/>
		</AdminPanelPage>
	);
};

export default LocationPage;
